import styled, { css, keyframes } from 'styled-components';
import { Trilha } from '../../components/Trilha';

/* Medidas e cores do protótipo de unidades (copasul-nossas-unidades.html),
   valendo só dentro desta página. Nomes das cores iguais aos de Notícias e
   Galeria; o fundo é o "papel" claro do protótipo, para o quadro dos filtros
   (branco) se destacar. */
export const Pagina = styled.div`
  --verde: #00893a;
  --verde-escuro: #0f4e2b;
  --verde-claro: #b2d7af;
  --nevoa: #eaf6ee;
  --tinta: #143e2d;
  --corpo: #43594d;
  --apagado: #6b7b72;
  --linha: #dce5dd;
  --borda: #c3ccc7;
  --papel: #f8faf9;
  --amarelo: #fbb81c;
  --gutter: clamp(1.375rem, 4vw, 2.5rem);

  background: var(--papel);
  color: var(--tinta);
  font-family: 'Inter', system-ui, -apple-system, 'Segoe UI', Roboto, sans-serif;
  text-align: left;

  a {
    color: inherit;
    text-decoration: none;
  }

  button,
  input,
  select {
    font: inherit;
    color: inherit;
  }

  button {
    cursor: pointer;
    background: transparent;
  }

  a:focus-visible,
  button:focus-visible {
    outline: 3px solid var(--amarelo);
    outline-offset: 4px;
  }

  img {
    display: block;
  }

  @media (prefers-reduced-motion: reduce) {
    *,
    *::before,
    *::after {
      transition: none !important;
      animation: none !important;
    }
  }
`;

export const Container = styled.div`
  width: 100%;
  max-width: 1360px;
  margin-inline: auto;
  padding-inline: var(--gutter);
  box-sizing: border-box;
`;

export const TrilhaDaPagina = styled(Trilha)`
  padding-top: clamp(1.5rem, 3vw, 2.2rem);
`;

export const Chapeu = styled.p`
  display: flex;
  align-items: center;
  gap: 0.6rem;
  margin: 0;
  font-size: 0.625rem;
  font-weight: 700;
  letter-spacing: 0.16em;
  text-transform: uppercase;
  color: var(--tinta);

  &::before {
    content: '';
    width: 1.75rem;
    height: 3px;
    border-radius: 2px;
    background: var(--amarelo);
  }
`;

/* ------------------------------------------------------------- abertura */

export const Abertura = styled.section`
  display: flex;
  justify-content: space-between;
  align-items: flex-end;
  gap: 2rem;
  margin: 2.4rem 0 2.25rem;

  h1 {
    margin: 0 0 1rem;
    font-size: clamp(2.6rem, 4.6vw, 4.05rem);
    font-weight: 500;
    line-height: 1.08;
    letter-spacing: -0.03em;
    text-wrap: balance;
  }

  h1 em {
    font-style: normal;
    color: var(--verde);
  }

  /* o título de Notícias quebra a linha antes do destaque; aqui é uma linha só */
  h1 br {
    display: none;
  }

  p {
    max-width: 37.5rem;
    margin: 0;
    font-size: 0.9375rem;
    line-height: 1.8;
    color: #616160;
  }

  @media (max-width: 720px) {
    display: block;
    margin: 2rem 0 1.75rem;

    h1 {
      font-size: 2.6rem;
    }

    p {
      font-size: 0.875rem;
    }
  }
`;

export const Presenca = styled.div`
  display: flex;
  align-items: center;
  gap: 0.75rem;
  flex-shrink: 0;
  padding-bottom: 0.4rem;
  color: var(--verde);

  strong {
    font-size: 0.9375rem;
    font-weight: 600;
    color: var(--tinta);
  }

  @media (max-width: 900px) {
    display: none;
  }
`;

/* --------------------------------------------------------------- filtros */

const campo = css`
  width: 100%;
  height: 3rem;
  box-sizing: border-box;
  padding: 0 0.9rem;
  border: 1px solid var(--borda);
  border-radius: 5px;
  background: #fff;
  color: var(--corpo);
  transition: border-color 0.15s, box-shadow 0.15s;

  &:focus {
    outline: none;
    border-color: var(--verde);
    box-shadow: 0 0 0 3px var(--nevoa);
  }

  /* 16px no celular: evita o zoom automático do iPhone ao tocar no campo */
  @media (max-width: 720px) {
    font-size: 1rem;
  }
`;

export const Filtros = styled.section`
  display: grid;
  grid-template-columns: 2fr 1fr 1fr;
  gap: 1.25rem;
  padding: 1.45rem;
  border: 1px solid var(--linha);
  border-radius: 8px;
  background: #fff;

  label {
    display: block;
    margin-bottom: 0.55rem;
    font-size: 0.75rem;
    font-weight: 600;
  }

  /* Seta própria: a do navegador fica colada na borda. Aqui ela tem o mesmo
     respiro da lupa no campo de busca. */
  select {
    ${campo}
    appearance: none;
    padding-right: 2.6rem;
    background: #fff
      url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='16' height='16' viewBox='0 0 24 24' fill='none' stroke='%236b7b72' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'%3E%3Cpath d='m6 9 6 6 6-6'/%3E%3C/svg%3E")
      no-repeat right 0.9rem center;
    cursor: pointer;
  }

  /* Celular: um campo embaixo do outro, cada um na largura toda. */
  @media (max-width: 720px) {
    grid-template-columns: 1fr;
    gap: 1rem;
    padding: 1.1rem;
  }
`;

export const CampoBusca = styled.div`
  position: relative;

  svg {
    position: absolute;
    left: 0.9rem;
    top: 50%;
    transform: translateY(-50%);
    color: var(--apagado);
    pointer-events: none;
  }

  input {
    ${campo}
    padding-left: 2.6rem;
  }
`;

export const Resultado = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1rem;
  margin: 1.8rem 0 1.25rem;
  font-size: 0.8125rem;
  color: var(--apagado);

  strong {
    font-weight: 600;
    color: var(--tinta);
  }

  button {
    display: inline-flex;
    align-items: center;
    gap: 0.3rem;
    padding: 0.4rem 0;
    border: 0;
    font-size: 0.8125rem;
    color: var(--verde);
  }

  button:hover {
    text-decoration: underline;
    text-underline-offset: 3px;
  }

  small {
    font-size: 0.8125rem;
  }

  @media (max-width: 720px) {
    small {
      display: none;
    }
  }
`;

/* ---------------------------------------------------------------- cartões */

export const Grade = styled.section`
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 1.55rem;
  padding-bottom: 1rem;

  @media (max-width: 1000px) {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }

  @media (max-width: 620px) {
    grid-template-columns: 1fr;
    gap: 1.35rem;
  }
`;

/* O cartão inteiro reage ao mouse (sobe, a foto aproxima, a seta anda), mas só
   a foto e o "Ver localização" são clicáveis: o resto é texto, que dá para
   selecionar e copiar (endereço, telefone, e-mail). */
export const Cartao = styled.article`
  display: flex;
  flex-direction: column;
  border-radius: 7px;
  overflow: hidden;
  background: var(--verde-escuro);
  color: #fff;
  transition: transform 0.2s ease, box-shadow 0.2s ease;

  &:hover {
    transform: translateY(-4px);
    box-shadow: 0 10px 24px rgba(10, 58, 31, 0.1);
  }

  &:hover img {
    transform: scale(1.035);
  }
`;

export const CartaoFoto = styled.button`
  position: relative;
  display: block;
  width: 100%;
  height: 13rem;
  padding: 0;
  border: 0;
  overflow: hidden;
  && {
    background: #0a3a1f;
  }

  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
    transition: transform 0.4s ease;
  }

  span {
    position: absolute;
    top: 1.05rem;
    left: 1.1rem;
    padding: 0.45rem 0.65rem;
    border-radius: 3px;
    background: rgba(255, 254, 241, 0.96);
    font-size: 0.625rem;
    font-weight: 700;
    letter-spacing: 0.08em;
    text-transform: uppercase;
    color: var(--tinta);
  }

  @media (min-width: 1500px) {
    height: 14.4rem;
  }
`;

export const CartaoCorpo = styled.div`
  display: flex;
  flex: 1;
  flex-direction: column;
  padding: 1.4rem 1.45rem 0;

  > p {
    margin: 0;
    font-size: 0.625rem;
    font-weight: 600;
    letter-spacing: 0.15em;
    text-transform: uppercase;
    color: var(--verde-claro);
  }

  h2 {
    margin: 0.5rem 0 1.15rem;
    font-size: 1.25rem;
    font-weight: 700;
    line-height: 1.25;
    letter-spacing: -0.02em;
  }

  @media (max-width: 620px) {
    h2 {
      font-size: 1.375rem;
    }
  }
`;

export const Contato = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 0.65rem;
  margin: 0.6rem 0;
  font-size: 0.75rem;
  line-height: 1.6;
  color: #e4eee7;
  overflow-wrap: anywhere;

  svg {
    flex-shrink: 0;
    margin-top: 0.15rem;
    color: var(--verde-claro);
  }

  small {
    display: block;
    margin-bottom: 0.2rem;
    font-size: 0.625rem;
    color: var(--apagado);
  }

  /* respiro antes do rodapé do cartão */
  &:last-of-type {
    margin-bottom: 1.35rem;
  }
`;

/* Fica no pé do cartão mesmo quando um cartão tem menos contatos que o vizinho. */
export const CartaoRodape = styled.button`
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
  margin-top: auto;
  padding: 1rem 0;
  border: 0;
  border-top: 1px solid rgba(255, 255, 255, 0.15);
  font-size: 0.75rem;
  font-weight: 600;
  text-align: left;

  && {
    color: #fff;
  }

  svg {
    transition: transform 0.2s ease;
  }

  ${Cartao}:hover & svg {
    transform: translateX(4px);
  }
`;

/* "Carregar mais" e a linha de fim: os mesmos da galeria. */
export const CarregarMais = styled.button`
  display: block;
  margin: 2.6rem auto 0;
  padding: 0.85rem 1.6rem;
  border: 1px solid var(--verde-escuro);
  border-radius: 4px;
  font-size: 0.8125rem;
  transition: background 0.2s, color 0.2s;

  && {
    background: transparent;
    color: var(--verde-escuro);
  }

  &&:hover {
    background: var(--verde-escuro);
    color: #fff;
  }
`;

export const Fim = styled.p`
  display: flex;
  align-items: center;
  gap: 1.25rem;
  margin: 2.6rem 0 0;
  font-size: 0.625rem;
  letter-spacing: 0.09em;
  text-transform: uppercase;
  color: var(--apagado);

  &::before,
  &::after {
    content: '';
    flex: 1;
    height: 1px;
    background: var(--linha);
  }
`;

export const Vazio = styled.div`
  grid-column: 1 / -1;
  padding: 4.4rem 1.5rem;
  border-radius: 8px;
  background: var(--nevoa);
  text-align: center;

  h2 {
    margin: 0 0 0.5rem;
    font-size: 1.25rem;
    font-weight: 600;
  }

  p {
    margin: 0;
    font-size: 0.875rem;
    color: var(--apagado);
  }
`;

/* ----------------------------------------------------------------- painel */

const entrar = keyframes`
  from { transform: translateX(100%); }
  to { transform: none; }
`;

export const Painel = styled.dialog`
  position: fixed;
  inset: 0 0 0 auto;
  width: min(35rem, 100%);
  max-width: 100%;
  height: 100%;
  max-height: 100%;
  margin: 0;
  padding: 0;
  border: 0;
  background: #fff;
  color: var(--tinta);
  box-shadow: -10px 0 60px rgba(0, 0, 0, 0.12);
  animation: ${entrar} 0.25s ease;

  &::backdrop {
    background: rgba(8, 45, 32, 0.63);
    backdrop-filter: blur(3px);
  }

  a {
    color: inherit;
  }

  a:focus-visible,
  button:focus-visible {
    outline: 3px solid var(--amarelo);
    outline-offset: 3px;
  }
`;

export const PainelConteudo = styled.div`
  height: 100%;
  overflow-y: auto;
`;

export const PainelTopo = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 1.25rem;
  padding: 1.75rem 1.9rem;

  h2 {
    margin: 0.65rem 0 0.3rem;
    font-size: 1.8rem;
    font-weight: 700;
    line-height: 1.15;
    letter-spacing: -0.03em;
  }

  span {
    font-size: 0.75rem;
    color: var(--apagado);
  }

  button {
    display: grid;
    place-items: center;
    flex-shrink: 0;
    width: 2.25rem;
    height: 2.25rem;
    padding: 0;
    border: 1px solid var(--linha);
    border-radius: 50%;
    background: var(--papel);
    color: var(--tinta);
    cursor: pointer;
  }

  @media (max-width: 620px) {
    padding: 1.5rem;

    h2 {
      font-size: 1.6rem;
    }
  }
`;

export const Mapa = styled.div`
  height: 19.4rem;
  background: #eef2ef;

  iframe {
    display: block;
    width: 100%;
    height: 100%;
    border: 0;
  }

  @media (max-width: 620px) {
    height: 17.8rem;
  }
`;

export const LegendaMapa = styled.p`
  margin: 0;
  padding: 0.65rem 1.9rem;
  background: var(--nevoa);
  font-size: 0.625rem;
  color: var(--corpo);

  @media (max-width: 620px) {
    padding-inline: 1.5rem;
  }
`;

export const Detalhes = styled.div`
  padding: 1.6rem 1.9rem 2rem;

  h3 {
    margin: 0 0 1.1rem;
    font-size: 0.75rem;
    font-weight: 600;
  }

  ${Contato} {
    margin: 1.2rem 0;
    font-size: 0.8125rem;
    color: var(--corpo);
  }

  ${Contato} svg {
    color: var(--verde);
  }

  ${Contato} a:hover {
    text-decoration: underline;
    text-underline-offset: 3px;
  }

  @media (max-width: 620px) {
    padding: 1.5rem;
  }
`;

export const Rota = styled.a`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.6rem;
  margin-top: 1.6rem;
  padding: 1.05rem;
  border-radius: 5px;
  font-weight: 600;
  transition: background 0.2s;

  && {
    background: var(--verde-escuro);
    color: #fff;
  }

  &&:hover {
    background: #0a3a1f;
  }
`;
